import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Icon, SimpleGrid, Divider, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure,
  Input, Select, NumberInput, NumberInputField,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { FiRefreshCw, FiPlus, FiHeart, FiCheckCircle, FiXCircle, FiTrash2 } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function HealthEndpoints() {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingAll, setCheckingAll] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState({ name: '', url: '', method: 'GET', expected_status: 200, timeout_seconds: 10 });
  const toast = useToast();

  const fetchEndpoints = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/health-endpoints/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setEndpoints(data.results || data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEndpoints(); }, [fetchEndpoints]);

  const checkAll = async () => {
    setCheckingAll(true);
    try {
      await fetch(`${API_BASE}/health-endpoints/check_all/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      toast({ title: 'Health checks completed', status: 'success', duration: 3000 });
      fetchEndpoints();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
    setCheckingAll(false);
  };

  const checkOne = async (id) => {
    try {
      await fetch(`${API_BASE}/health-endpoints/${id}/check/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      toast({ title: 'Check completed', status: 'success', duration: 2000 });
      fetchEndpoints();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const createEndpoint = async () => {
    try {
      const res = await fetch(`${API_BASE}/health-endpoints/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: 'Endpoint created', status: 'success', duration: 3000 });
      onClose();
      setForm({ name: '', url: '', method: 'GET', expected_status: 200, timeout_seconds: 10 });
      fetchEndpoints();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const deleteEndpoint = async (id, name) => {
    if (!window.confirm(`Delete health endpoint "${name}"?`)) return;
    try {
      await fetch(`${API_BASE}/health-endpoints/${id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      toast({ title: 'Endpoint deleted', status: 'success', duration: 3000 });
      fetchEndpoints();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;

  const healthyCount = endpoints.filter(e => e.latest_result?.is_healthy).length;
  const unhealthyCount = endpoints.filter(e => e.latest_result && !e.latest_result.is_healthy).length;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Health Endpoints</Heading>
          <Text color="gray.500">
            {endpoints.length} endpoints —{' '}
            <Badge colorScheme="green">{healthyCount} healthy</Badge>{' '}
            <Badge colorScheme="red">{unhealthyCount} unhealthy</Badge>
          </Text>
        </VStack>
        <HStack>
          <Button leftIcon={<FiHeart />} colorScheme="green" onClick={checkAll} isLoading={checkingAll}>
            Check All
          </Button>
          <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onOpen}>
            Add Endpoint
          </Button>
          <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchEndpoints}>Refresh</Button>
        </HStack>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Status</Th>
              <Th>Name</Th>
              <Th>URL</Th>
              <Th>Method</Th>
              <Th>Response Time</Th>
              <Th>Last Check</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {endpoints.map((ep) => (
              <Tr key={ep.id} _hover={{ bg: 'gray.50' }}>
                <Td>
                  {ep.latest_result ? (
                    <Icon
                      as={ep.latest_result.is_healthy ? FiCheckCircle : FiXCircle}
                      color={ep.latest_result.is_healthy ? 'green.500' : 'red.500'}
                      boxSize={5}
                    />
                  ) : (
                    <Badge colorScheme="gray">No data</Badge>
                  )}
                </Td>
                <Td><Text fontWeight="medium" fontSize="sm">{ep.name}</Text></Td>
                <Td><Text fontSize="xs" fontFamily="mono" color="gray.600" isTruncated maxW="300px">{ep.url}</Text></Td>
                <Td><Badge>{ep.method}</Badge></Td>
                <Td>
                  {ep.latest_result?.response_time_ms != null
                    ? <Text fontSize="sm">{ep.latest_result.response_time_ms} ms</Text>
                    : '—'}
                </Td>
                <Td>
                  <Text fontSize="xs" color="gray.500">
                    {ep.latest_result?.checked_at
                      ? new Date(ep.latest_result.checked_at).toLocaleString()
                      : '—'}
                  </Text>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <Button size="xs" colorScheme="green" variant="ghost" onClick={() => checkOne(ep.id)}>
                      Check
                    </Button>
                    <Button size="xs" colorScheme="red" variant="ghost" onClick={() => deleteEndpoint(ep.id, ep.name)}>
                      <FiTrash2 />
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Add Endpoint Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Health Endpoint</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="My Service" />
              </FormControl>
              <FormControl>
                <FormLabel>URL</FormLabel>
                <Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://..." />
              </FormControl>
              <FormControl>
                <FormLabel>Method</FormLabel>
                <Select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="HEAD">HEAD</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Expected Status Code</FormLabel>
                <NumberInput value={form.expected_status} onChange={(_, v) => setForm({ ...form, expected_status: v })} min={100} max={599}>
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={createEndpoint}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default HealthEndpoints;
